<script setup>
import { useQuality } from '@/composables/useQuality'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Risk',
  },
})

const ability = useAbility()
const { risks, loading, saving, fetchRisks, createRisk, updateRisk, deleteRisk } = useQuality()

onMounted(() => fetchRisks())

const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const errors = ref({})

const formDefault = () => ({ unit_id: null, title: '', description: '', category: '', likelihood: 1, impact: 1, mitigation_plan: '', status: 'identified' })
const form = ref(formDefault())

const statusOptions = [
  { title: 'Teridentifikasi', value: 'identified' },
  { title: 'Mitigasi', value: 'mitigating' },
  { title: 'Selesai', value: 'closed' },
]

const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  errors.value = {}
  dialogTitle.value = 'Tambah Risiko'
  dialog.value = true
}

const openEdit = item => {
  editingId.value = item.id
  form.value = {
    unit_id: item.unit?.id ?? null,
    title: item.title,
    description: item.description ?? '',
    category: item.category ?? '',
    likelihood: item.likelihood,
    impact: item.impact,
    mitigation_plan: item.mitigation_plan ?? '',
    status: item.status,
  }
  errors.value = {}
  dialogTitle.value = 'Edit Risiko'
  dialog.value = true
}

const save = async () => {
  errors.value = {}
  try {
    if (editingId.value) await updateRisk(editingId.value, form.value)
    else await createRisk(form.value)
    dialog.value = false
    fetchRisks()
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save risk error:', e)
  }
}

const deleteDialog = ref(false)
const deletingItem = ref(null)
const deleting = ref(false)

const confirmDelete = item => { deletingItem.value = item; deleteDialog.value = true }

const doDelete = async () => {
  deleting.value = true
  try {
    await deleteRisk(deletingItem.value.id)
    deleteDialog.value = false
    fetchRisks()
  } catch (e) {
    console.error('Delete risk error:', e)
  } finally {
    deleting.value = false
  }
}

const scoreColor = score => {
  if (score >= 15) return 'error'
  if (score >= 8) return 'warning'

  return 'success'
}

const headers = [
  { title: 'Judul', key: 'title' },
  { title: 'Kategori', key: 'category' },
  { title: 'Unit', key: 'unit' },
  { title: 'L × I', key: 'li', align: 'center' },
  { title: 'Skor', key: 'risk_score', align: 'center' },
  { title: 'Status', key: 'status', align: 'center' },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 100 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Risk Register
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Skor risiko = likelihood × impact, skala 1-5 (§9.4)
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'Risk')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Risiko
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="risks"
        :loading="loading"
        hide-default-footer
      >
        <template #item.category="{ item }">
          {{ item.category ?? '-' }}
        </template>
        <template #item.unit="{ item }">
          {{ item.unit?.name ?? '-' }}
        </template>
        <template #item.li="{ item }">
          {{ item.likelihood }} × {{ item.impact }}
        </template>
        <template #item.risk_score="{ item }">
          <VChip
            size="small"
            :color="scoreColor(item.risk_score)"
          >
            {{ item.risk_score }}
          </VChip>
        </template>
        <template #item.status="{ item }">
          <VChip
            size="small"
            variant="tonal"
          >
            {{ item.status }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'Risk')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('manage', 'Risk')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data risiko
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="640"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ dialogTitle }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.title"
                label="Judul Risiko *"
                :error-messages="errors.title"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.description"
                label="Deskripsi"
                rows="2"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <MasterDataPicker
                v-model="form.unit_id"
                entity="units"
                label="Unit"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.category"
                label="Kategori"
                placeholder="mis. klinis, non-klinis, K3"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="form.likelihood"
                :items="[1, 2, 3, 4, 5]"
                label="Likelihood (1-5) *"
                :error-messages="errors.likelihood"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="form.impact"
                :items="[1, 2, 3, 4, 5]"
                label="Impact (1-5) *"
                :error-messages="errors.impact"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="form.status"
                :items="statusOptions"
                label="Status"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.mitigation_plan"
                label="Rencana Mitigasi"
                rows="2"
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Risiko?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingItem?.title }}</strong> akan dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="deleting"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
